import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence, useDragControls } from 'framer-motion'
import {
  X, User as UserIcon, Lock, Eye, EyeOff, CheckCircle,
  Shield, Monitor, Smartphone, Laptop, Globe, LogOut, RefreshCw,
} from 'lucide-react'
import { useMailStore } from '../../stores/mailStore'
import { useAccountStore } from '../../stores/accountStore'
import { auth as authApi, setAccessToken, clearAccessToken, type SessionInfo } from '../../lib/api'

// ─── Silent token refresh ─────────────────────────────────────────────────────
// Called once on app mount — refreshes the access token proactively every
// 12 minutes (access token TTL is 15 min), so the user is never interrupted.
//
// Multi-account aware: uses the active account's per-account refresh token
// (sent in the request body) instead of the shared HTTP-only cookie, so
// switching accounts doesn't refresh as the wrong user.
export function useSilentRefresh() {
  useEffect(() => {
    const INTERVAL = 12 * 60 * 1000 // 12 minutes

    // Use a ref-like variable so the interval callback can clear itself
    let id: ReturnType<typeof setInterval>

    const refresh = async () => {
      // Skip if there's no token — user is logged out
      if (!localStorage.getItem('access_token')) {
        clearInterval(id)
        return
      }

      const activeAccount = useAccountStore.getState().getActiveAccount()
      const perAccountToken = activeAccount?.refreshToken ?? ''

      // Always ensure localStorage reflects the active account's current token.
      // This corrects any drift that happened between account switches.
      if (activeAccount?.accessToken) {
        setAccessToken(activeAccount.accessToken)
      }

      // If the active account has no per-account refresh token (it was reconnected
      // from localStorage on page reload and never freshly logged in this session),
      // do NOT fall back to the shared cookie — that cookie belongs to whichever
      // account last logged in and would overwrite the active account's token.
      // The access token remains valid for 15 min; the user will be prompted to
      // re-login only if it actually expires.
      if (!perAccountToken) return

      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL ?? ''}/auth/refresh`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: perAccountToken }),
          }
        )
        if (res.ok) {
          const data = await res.json()
          setAccessToken(data.access_token)
          // Keep the store in sync
          if (activeAccount?.email) {
            useAccountStore.getState().updateAccount(activeAccount.email, { accessToken: data.access_token })
          }
        } else if (res.status === 401) {
          // Refresh token expired or revoked — stop retrying and log out
          clearInterval(id)
          clearAccessToken()
          window.dispatchEvent(new Event('auth:logout'))
        }
        // Any other non-ok status (5xx etc.) — will retry next interval
      } catch {
        // Network blip — will retry next interval
      }
    }

    id = setInterval(refresh, INTERVAL)
    return () => clearInterval(id)
  }, [])
}

// ─── User-agent parser ────────────────────────────────────────────────────────
function parseUA(ua: string): { browser: string; os: string; icon: 'phone' | 'tablet' | 'desktop' } {
  const s = ua.toLowerCase()

  // OS
  let os = 'Unknown OS'
  if (s.includes('iphone')) os = 'iPhone'
  else if (s.includes('ipad')) os = 'iPad'
  else if (s.includes('android')) os = 'Android'
  else if (s.includes('mac os')) os = 'macOS'
  else if (s.includes('windows')) os = 'Windows'
  else if (s.includes('linux')) os = 'Linux'
  else if (s.includes('chromeos') || s.includes('cros')) os = 'ChromeOS'

  // Browser
  let browser = 'Unknown Browser'
  if (s.includes('edg/') || s.includes('edge/')) browser = 'Edge'
  else if (s.includes('opr/') || s.includes('opera')) browser = 'Opera'
  else if (s.includes('brave')) browser = 'Brave'
  else if (s.includes('chrome')) browser = 'Chrome'
  else if (s.includes('firefox')) browser = 'Firefox'
  else if (s.includes('safari') && !s.includes('chrome')) browser = 'Safari'
  else if (s.includes('curl')) browser = 'curl'
  else if (s.includes('thunderbird')) browser = 'Thunderbird'

  // Icon
  let icon: 'phone' | 'tablet' | 'desktop' = 'desktop'
  if (os === 'iPhone' || (os === 'Android' && !s.includes('tablet'))) icon = 'phone'
  else if (os === 'iPad') icon = 'tablet'

  return { browser, os, icon }
}

function DeviceIcon({ type, size = 16 }: { type: 'phone' | 'tablet' | 'desktop'; size?: number }) {
  if (type === 'phone') return <Smartphone size={size} />
  if (type === 'tablet') return <Laptop size={size} />
  return <Monitor size={size} />
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'Active now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

// ─── Sessions tab ─────────────────────────────────────────────────────────────
function SessionsTab() {
  const [sessions, setSessions] = useState<SessionInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [revoking, setRevoking] = useState<string | null>(null)
  const [revokeAllLoading, setRevokeAllLoading] = useState(false)
  const [success, setSuccess] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await authApi.sessions()
      setSessions(data.sessions)
    } catch {
      setError('Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleRevoke = async (id: string) => {
    setRevoking(id)
    setSuccess('')
    try {
      await authApi.revokeSession(id)
      setSessions(s => s.filter(x => x.id !== id))
      setSuccess('Session revoked')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to revoke session')
    } finally {
      setRevoking(null)
    }
  }

  const handleRevokeAll = async () => {
    setRevokeAllLoading(true)
    setSuccess('')
    setError('')
    try {
      const res = await authApi.revokeAllOtherSessions()
      setSessions(s => s.filter(x => x.is_current))
      setSuccess(`Signed out of ${res.revoked} other ${res.revoked === 1 ? 'session' : 'sessions'}`)
    } catch {
      setError('Failed to sign out other sessions')
    } finally {
      setRevokeAllLoading(false)
    }
  }

  const otherCount = sessions.filter(s => !s.is_current).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          {loading ? 'Loading…' : `${sessions.length} active ${sessions.length === 1 ? 'session' : 'sessions'}`}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn--secondary" onClick={load} style={{ padding: '5px 10px', fontSize: 12 }}>
            <RefreshCw size={12} />
          </button>
          {otherCount > 0 && (
            <button
              className="btn btn--danger-outline"
              onClick={handleRevokeAll}
              disabled={revokeAllLoading}
              style={{ padding: '5px 12px', fontSize: 12 }}
            >
              {revokeAllLoading ? 'Signing out…' : `Sign out ${otherCount} other ${otherCount === 1 ? 'session' : 'sessions'}`}
            </button>
          )}
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success">
          <CheckCircle size={13} /> {success}
        </div>
      )}

      {/* Session list */}
      {loading ? (
        <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
          Loading sessions…
        </div>
      ) : sessions.length === 0 ? (
        <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
          No active sessions found
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sessions.map(sess => {
            const { browser, os, icon } = parseUA(sess.user_agent || '')
            const isActive = Date.now() - new Date(sess.last_active_at).getTime() < 5 * 60_000
            return (
              <div
                key={sess.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  background: sess.is_current
                    ? 'color-mix(in srgb, var(--brand) 6%, var(--bg-elevated))'
                    : 'var(--bg-elevated)',
                  border: `1px solid ${sess.is_current ? 'color-mix(in srgb, var(--brand) 20%, transparent)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius-md)',
                }}
              >
                {/* Device icon */}
                <div style={{
                  width: 36, height: 36, borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-panel)', border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: sess.is_current ? 'var(--brand)' : 'var(--text-tertiary)',
                  flexShrink: 0,
                }}>
                  <DeviceIcon type={icon} size={17} />
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                      {browser} on {os}
                    </span>
                    {sess.is_current && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px',
                        borderRadius: 'var(--radius-full)',
                        background: 'color-mix(in srgb, var(--brand) 15%, transparent)',
                        color: 'var(--brand)',
                      }}>
                        THIS DEVICE
                      </span>
                    )}
                    {isActive && !sess.is_current && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px',
                        borderRadius: 'var(--radius-full)',
                        background: 'color-mix(in srgb, #22c55e 15%, transparent)',
                        color: '#22c55e',
                      }}>
                        ACTIVE NOW
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {sess.ip_address && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Globe size={10} /> {sess.ip_address}
                      </span>
                    )}
                    <span>Signed in {timeAgo(sess.created_at)}</span>
                    <span>Active {timeAgo(sess.last_active_at)}</span>
                  </div>
                </div>

                {/* Revoke */}
                {!sess.is_current && (
                  <button
                    className="btn btn--danger-outline"
                    onClick={() => handleRevoke(sess.id)}
                    disabled={revoking === sess.id}
                    style={{ padding: '5px 10px', fontSize: 12, flexShrink: 0 }}
                    title="Sign out this session"
                  >
                    {revoking === sess.id ? '…' : <LogOut size={13} />}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: 0 }}>
        Sessions expire after 30 days of inactivity. Revoking a session signs that device out immediately.
      </p>
    </div>
  )
}

// ─── ProfileModal ─────────────────────────────────────────────────────────────

interface ProfileModalProps {
  open: boolean
  onClose: () => void
}

export function ProfileModal({ open, onClose }: ProfileModalProps) {
  const { user } = useMailStore()
  const [tab, setTab] = useState<'profile' | 'password' | 'sessions'>('profile')

  // Profile tab
  const [name, setName] = useState(user?.name ?? '')
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileError, setProfileError] = useState('')
  const [profileSuccess, setProfileSuccess] = useState(false)

  // Password tab
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  if (!user) return null

  const handleProfileSave = async () => {
    setProfileError('')
    setProfileSuccess(false)
    if (!name.trim()) { setProfileError('Name cannot be empty'); return }
    setProfileLoading(true)
    try {
      const updated = await authApi.updateMe({ name: name.trim() })
      useMailStore.setState({ user: { ...user, name: updated.name } })
      setProfileSuccess(true)
    } catch (e: unknown) {
      setProfileError(e instanceof Error ? e.message : 'Failed to update profile')
    } finally {
      setProfileLoading(false)
    }
  }

  const handlePasswordSave = async () => {
    setPasswordError('')
    setPasswordSuccess(false)
    if (!currentPassword) { setPasswordError('Current password is required'); return }
    if (!newPassword) { setPasswordError('New password is required'); return }
    if (newPassword.length < 8) { setPasswordError('New password must be at least 8 characters'); return }
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return }
    setPasswordLoading(true)
    try {
      await authApi.updateMe({ current_password: currentPassword, new_password: newPassword })
      setPasswordSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (e: unknown) {
      setPasswordError(e instanceof Error ? e.message : 'Failed to change password')
    } finally {
      setPasswordLoading(false)
    }
  }

  const initials = user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
  const constraintsRef = useRef<HTMLDivElement>(null)
  const dragControls = useDragControls()

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Full-screen ref layer for drag boundary constraints */}
          <div
            ref={constraintsRef}
            style={{ position: 'fixed', inset: 0, zIndex: 999, pointerEvents: 'none' }}
          />
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ zIndex: 1000 }}
          />
          <motion.div
            className="modal profile-modal"
            drag
            dragControls={dragControls}
            dragListener={false}
            dragMomentum={false}
            dragElastic={0}
            dragConstraints={constraintsRef}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{ zIndex: 1001 }}
          >
            {/* Header — drag handle */}
            <div
              className="modal__header profile-modal__drag-handle"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <h2>Profile &amp; Settings</h2>
              <button className="btn-icon" onClick={onClose}><X size={15} /></button>
            </div>

            {/* Avatar row */}
            <div className="profile-modal__avatar-row">
              <div className="profile-modal__avatar">{initials}</div>
              <div>
                <div className="profile-modal__avatar-name">{user.name}</div>
                <div className="profile-modal__avatar-email">{user.email}</div>
              </div>
            </div>

            {/* Tabs */}
            <div className="profile-modal__tabs">
              <button
                className={`profile-modal__tab${tab === 'profile' ? ' profile-modal__tab--active' : ''}`}
                onClick={() => { setTab('profile'); setProfileError(''); setProfileSuccess(false) }}
              >
                <UserIcon size={13} /> Profile
              </button>
              <button
                className={`profile-modal__tab${tab === 'password' ? ' profile-modal__tab--active' : ''}`}
                onClick={() => { setTab('password'); setPasswordError(''); setPasswordSuccess(false) }}
              >
                <Lock size={13} /> Change Password
              </button>
              <button
                className={`profile-modal__tab${tab === 'sessions' ? ' profile-modal__tab--active' : ''}`}
                onClick={() => setTab('sessions')}
              >
                <Shield size={13} /> Active Sessions
              </button>
            </div>

            {/* Tab content */}
            <div className="profile-modal__body">
              {tab === 'profile' && (
                <div className="profile-modal__form">
                  <div className="form-field">
                    <label className="form-label">Display Name</label>
                    <input
                      className="form-input"
                      value={name}
                      onChange={(e) => { setName(e.target.value); setProfileSuccess(false) }}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Email</label>
                    <input className="form-input form-input--readonly" value={user.email} readOnly />
                  </div>
                  {profileError && <div className="form-error">{profileError}</div>}
                  {profileSuccess && (
                    <div className="form-success">
                      <CheckCircle size={13} /> Profile updated
                    </div>
                  )}
                  <div className="profile-modal__actions">
                    <button className="btn btn--secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn--primary" onClick={handleProfileSave} disabled={profileLoading}>
                      {profileLoading ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {tab === 'password' && (
                <div className="profile-modal__form">
                  <div className="form-field">
                    <label className="form-label">Current Password</label>
                    <div className="form-input-wrap">
                      <input
                        className="form-input"
                        type={showCurrent ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Current password"
                        autoComplete="current-password"
                      />
                      <button type="button" className="form-input-eye" onClick={() => setShowCurrent(v => !v)} tabIndex={-1}>
                        {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="form-label">New Password</label>
                    <div className="form-input-wrap">
                      <input
                        className="form-input"
                        type={showNew ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                      />
                      <button type="button" className="form-input-eye" onClick={() => setShowNew(v => !v)} tabIndex={-1}>
                        {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Confirm New Password</label>
                    <input
                      className="form-input"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat new password"
                      autoComplete="new-password"
                    />
                  </div>
                  {passwordError && <div className="form-error">{passwordError}</div>}
                  {passwordSuccess && (
                    <div className="form-success">
                      <CheckCircle size={13} /> Password changed successfully
                    </div>
                  )}
                  <div className="profile-modal__actions">
                    <button className="btn btn--secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn--primary" onClick={handlePasswordSave} disabled={passwordLoading}>
                      {passwordLoading ? 'Saving…' : 'Change Password'}
                    </button>
                  </div>
                </div>
              )}

              {tab === 'sessions' && <SessionsTab />}
            </div>
          </motion.div>

          <style>{`
            .profile-modal {
              min-width: 480px;
              max-width: 540px;
            }

            .profile-modal__drag-handle {
              cursor: grab;
              user-select: none;
            }
            .profile-modal__drag-handle:active {
              cursor: grabbing;
            }

            .profile-modal__avatar-row {
              display: flex;
              align-items: center;
              gap: 14px;
              padding: 16px 20px 12px;
              border-bottom: 1px solid var(--border);
            }

            .profile-modal__avatar {
              width: 48px;
              height: 48px;
              border-radius: 50%;
              background: var(--brand);
              color: white;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 17px;
              font-weight: 700;
              flex-shrink: 0;
            }

            .profile-modal__avatar-name {
              font-size: 15px;
              font-weight: 600;
              color: var(--text-primary);
            }

            .profile-modal__avatar-email {
              font-size: 12px;
              color: var(--text-tertiary);
              margin-top: 2px;
            }

            .profile-modal__tabs {
              display: flex;
              gap: 2px;
              padding: 8px 20px 0;
              border-bottom: 1px solid var(--border);
            }

            .profile-modal__tab {
              display: flex;
              align-items: center;
              gap: 6px;
              padding: 7px 12px;
              border: none;
              background: transparent;
              color: var(--text-tertiary);
              font-family: var(--font-ui);
              font-size: 13px;
              cursor: pointer;
              border-bottom: 2px solid transparent;
              margin-bottom: -1px;
              transition: color var(--duration-fast), border-color var(--duration-fast);
            }

            .profile-modal__tab:hover { color: var(--text-primary); }

            .profile-modal__tab--active {
              color: var(--brand);
              border-bottom-color: var(--brand);
            }

            .profile-modal__body {
              padding: 20px;
              max-height: 420px;
              overflow-y: auto;
            }

            .profile-modal__form {
              display: flex;
              flex-direction: column;
              gap: 14px;
            }

            .profile-modal__actions {
              display: flex;
              justify-content: flex-end;
              gap: 8px;
              margin-top: 4px;
            }

            .form-field {
              display: flex;
              flex-direction: column;
              gap: 5px;
            }

            .form-label {
              font-size: 12px;
              font-weight: 500;
              color: var(--text-secondary);
            }

            .form-input-wrap { position: relative; }

            .form-input {
              width: 100%;
              padding: 8px 12px;
              background: var(--bg-elevated);
              border: 1px solid var(--border-strong);
              border-radius: var(--radius-md);
              color: var(--text-primary);
              font-family: var(--font-ui);
              font-size: 13px;
              outline: none;
              transition: border-color var(--duration-fast);
              box-sizing: border-box;
            }

            .form-input:focus {
              border-color: var(--brand);
              box-shadow: 0 0 0 2px var(--brand-subtle, rgba(79,142,247,.12));
            }

            .form-input-wrap .form-input { padding-right: 36px; }

            .form-input--readonly {
              opacity: 0.55;
              cursor: not-allowed;
            }

            .form-input-eye {
              position: absolute;
              right: 10px;
              top: 50%;
              transform: translateY(-50%);
              background: none;
              border: none;
              cursor: pointer;
              color: var(--text-tertiary);
              display: flex;
              align-items: center;
              padding: 2px;
            }

            .form-input-eye:hover { color: var(--text-primary); }

            .form-error {
              font-size: 12px;
              color: var(--red, #ef4444);
              padding: 8px 12px;
              background: rgba(239,68,68,.08);
              border: 1px solid rgba(239,68,68,.2);
              border-radius: var(--radius-md);
            }

            .form-success {
              display: flex;
              align-items: center;
              gap: 6px;
              font-size: 12px;
              color: var(--green, #22c55e);
              padding: 8px 12px;
              background: rgba(34,197,94,.08);
              border: 1px solid rgba(34,197,94,.2);
              border-radius: var(--radius-md);
            }

            .btn {
              display: inline-flex;
              align-items: center;
              gap: 5px;
              padding: 7px 16px;
              border-radius: var(--radius-md);
              font-family: var(--font-ui);
              font-size: 13px;
              font-weight: 500;
              cursor: pointer;
              border: none;
              transition: background var(--duration-fast), opacity var(--duration-fast);
            }

            .btn:disabled { opacity: 0.5; cursor: not-allowed; }

            .btn--primary { background: var(--brand); color: white; }
            .btn--primary:not(:disabled):hover { opacity: 0.88; }

            .btn--secondary {
              background: var(--bg-elevated);
              color: var(--text-secondary);
              border: 1px solid var(--border-strong);
            }
            .btn--secondary:hover { background: var(--bg-hover); color: var(--text-primary); }

            .btn--danger-outline {
              background: transparent;
              color: #ef4444;
              border: 1px solid color-mix(in srgb, #ef4444 35%, transparent);
            }
            .btn--danger-outline:not(:disabled):hover {
              background: color-mix(in srgb, #ef4444 10%, transparent);
            }
          `}</style>
        </>
      )}
    </AnimatePresence>
  )
}
